const elementName = 'aceii-product-anchor-nav';
const activeDocumentClass = 'aceii-product-anchor-nav-active';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiProductAnchorNav extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.sectionWrapper = this.closest('.shopify-section');
      this.viewport = this.querySelector('[data-anchor-nav-viewport]');
      this.links = [...this.querySelectorAll('[data-anchor-target]')];
      this.productSections = [...document.querySelectorAll('#MainContent > .shopify-section')];
      this.entries = this.links
        .map((link) => {
          const targetIdSuffix = `__${link.dataset.anchorTarget}`;
          const target = this.productSections.find(
            (section) => section.id.endsWith(targetIdSuffix),
          );
          const item = link.closest('li');

          if (item) item.hidden = !target;
          if (target) link.setAttribute('href', `#${target.id}`);

          return target ? { link, target } : null;
        })
        .filter(Boolean);

      if (!this.entries.length) {
        this.sectionWrapper.hidden = true;
        return;
      }

      this.currentLink = null;
      this.scrollTrackingPaused = false;
      this.scrollTrackingTimer = null;
      this.frame = null;

      this.entries.forEach(({ link, target }) => {
        link.addEventListener(
          'click',
          (event) => this.handleLinkClick(event, link, target),
          { signal },
        );
      });

      this.handleViewportChange = this.handleViewportChange.bind(this);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true, signal });
      window.addEventListener('resize', this.handleViewportChange, { passive: true, signal });

      this.resizeObserver = new ResizeObserver(() => this.updateMetrics());
      this.resizeObserver.observe(this);

      this.updateMetrics();
      this.updateState();
    }

    disconnectedCallback() {
      window.clearTimeout(this.scrollTrackingTimer);
      window.cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
      document.documentElement.classList.remove(activeDocumentClass);
      document.documentElement.style.removeProperty('--aceii-product-anchor-nav-height');
    }

    updateMetrics() {
      this.navHeight = this.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        '--aceii-product-anchor-nav-height',
        `${this.navHeight}px`,
      );
      this.updateState();
    }

    handleViewportChange() {
      if (this.frame) return;

      this.frame = window.requestAnimationFrame(() => {
        this.updateState();
        this.frame = null;
      });
    }

    updateState() {
      this.updateStickyState();
      if (!this.scrollTrackingPaused) this.updateActiveLink();
    }

    updateStickyState() {
      const top = this.sectionWrapper?.getBoundingClientRect().top ?? 1;
      const isStuck = top <= 0.5;

      this.classList.toggle('is-stuck', isStuck);
      document.documentElement.classList.toggle(activeDocumentClass, isStuck);
    }

    updateActiveLink() {
      const activationLine = this.navHeight + 2;
      let activeEntry = this.entries[0];

      for (const entry of this.entries) {
        if (entry.target.getBoundingClientRect().top <= activationLine) {
          activeEntry = entry;
        } else {
          break;
        }
      }

      this.setActiveLink(activeEntry.link);
    }

    handleLinkClick(event, link, target) {
      event.preventDefault();
      this.scrollTrackingPaused = true;
      this.setActiveLink(link, { center: true });

      const targetTop = window.scrollY + target.getBoundingClientRect().top - this.navHeight;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });

      window.clearTimeout(this.scrollTrackingTimer);
      this.scrollTrackingTimer = window.setTimeout(() => {
        this.scrollTrackingPaused = false;
        this.updateState();
      }, reducedMotion ? 0 : 900);
    }

    setActiveLink(link, { center = false } = {}) {
      if (!link || (link === this.currentLink && !center)) return;

      this.entries.forEach(({ link: entryLink }) => {
        const active = entryLink === link;
        entryLink.classList.toggle('is-active', active);
        if (active) {
          entryLink.setAttribute('aria-current', 'true');
        } else {
          entryLink.removeAttribute('aria-current');
        }
      });

      this.currentLink = link;
      this.centerLink(link, center ? 'smooth' : 'auto');
    }

    centerLink(link, behavior = 'smooth') {
      if (!this.viewport || this.viewport.scrollWidth <= this.viewport.clientWidth + 1) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const left = this.viewport.scrollLeft
        + linkRect.left
        - viewportRect.left
        - ((viewportRect.width - linkRect.width) / 2);

      this.viewport.scrollTo({ left, behavior });
    }
  }

  window.customElements.define(elementName, AceiiProductAnchorNav);
}
